import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api.js';

// Authentication is central (CXFOP SSO). This context only tracks the
// app-local session: /auth/me succeeds -> user; 401 -> the Login page
// redirects through /api/auth/oidc/login (silent while the platform SSO
// session is valid, per the "standard web app" contract profile).
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.auth.me();
      setUser(me);
    } catch (err) {
      if (err.status !== 401 && err.status !== 403) {
        console.warn('Auth refresh failed', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Best-effort: clearing the local session is the goal even when the
  // logout request itself fails, so this never throws — callers can
  // safely navigate to /login?signedout=1 afterwards.
  //
  // The sessionStorage flag guards against a render race: when the user
  // state flips to null, a protected route's RequireAuth can redirect to
  // a bare /login (losing the ?signedout=1 marker) before the caller's
  // own navigation lands — and a bare login page would auto-redirect
  // into a silent SSO re-login, making sign-out a no-op. The flag keeps
  // the signed-out state sticky for this tab regardless of which
  // navigation wins; the Login page clears it when the user explicitly
  // signs back in.
  const logout = async () => {
    try {
      sessionStorage.setItem('iptbc_signed_out', '1');
    } catch {
      /* storage unavailable — the ?signedout=1 param is the fallback */
    }
    try {
      await api.auth.logout();
    } catch (err) {
      console.warn('Logout request failed', err);
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
